<template src="./template.html"></template>
<style src="./styles.scss" lang="scss" scoped></style>

<script>
  import { mapActions, mapState } from 'vuex'
  import { clone, isEqual, forEach, filter, findIndex, find } from 'lodash'
  import { changeBool, insertEmoji } from '@/helper'
  import api from '@/api'
  import AttachmentItem from './AttachmentItem'
  import FileUploader from '@/components/ui/FileUploader'
  import LinkPreview from '@/components/ui/LinkPreview'
  import SingleSelectInput from '@/components/ui/SingleSelectInput'
  import VEmojiPicker from 'v-emoji-picker'

  export default {
    name: 'EditPostOverlay',
    components: {
      AttachmentItem,
      FileUploader,
      LinkPreview,
      MentionInput: () => import('@/components/ui/MentionInput'),
      SingleSelectInput,
      VEmojiPicker,
    },
    props: {
      postId: String,
      isOpen: Boolean,
    },
    data() {
      return {
        files: [],
        fileUploaderConfig: {
          // Should add config properties fileUploaderConfig which different from FileUploader's baseConfig settings
        },
        isPendingPreview: false,
        isPendingEdit: false,
        link: '',
        linkPreview: {},
        options: [],
        post: {},
        postOptions: {},
        selectedAuthor: null,
        emojiOpen: false,
      }
    },
    computed: {
      ...mapState('news', [
        'newsItem',
        'pending',
      ]),
      ...mapState('auth', { activeUser: 'user' }),
      ...mapState('auth', ['settings']),
      ...mapState('posts', ['authors']),
      opened: {
        set() {
          this.$emit('onCloseOverlay')
        },
        get() {
          return this.isOpen
        },
      },
      allFiles() {
        const allFiles = []
        forEach(this.post.attachments, a => allFiles.push(a.id))
        forEach(this.files, a => allFiles.push(a))
        return allFiles
      },
    },
    watch: {
      'postOptions.types': {
        handler(val, oldVal) {
          // if val is true and val is not the oldVal
          if (Boolean(val) && !isEqual(val, oldVal)) {
            // check key exists, to get the topic options by types key
            if (val.key) {
              // delete the selected old topics, when types key has changed
              this.$delete(this.postOptions, 'topics')
              this.getTopicOptions(val.key)
            }
          } else if (!isEqual(val, oldVal)) {
            // delete selected topics and types, when val is false, for example val = null
            this.$delete(this.postOptions, 'topics')
            this.$delete(this.postOptions, 'types')
            this.removeTopics()
          }
        },
        deep: true,
      },
      'post.link_previews': {
        handler(preview) {
          if (preview && preview.length) {
            this.link = preview[0].data.url
            this.linkPreview = preview[0].data
          }
        },
        deep: true,
        immediate: true,
      },
    },
    created() {
      this.getNewsItem(this.postId).then(_ => {
        this.post = clone(this.newsItem)
        this.getOptions()
        this.getAuthors().then(_ => {
          this.selectedAuthor = find(this.authors, { id: this.post.author.id }) || null
        })
      })
    },
    methods: {
      ...mapActions('news', [ 'getNewsItem' ]),
      ...mapActions('posts', [ 'getAuthors' ]),
      async getOptions() {
        await api.items.getItems('posts.options').then(({ data }) => {
          this.options = filter(data, o => o.identifier === 'types')
        })
      },
      async getTopicOptions(id) {
        await api.items.getItem(`posts.options.${id}`).then(({ data }) => {
          const topicIndex = findIndex(this.options, o => o.id === data[0].id)
          if (topicIndex === -1) {
            this.options.splice(1, 0, data[0])
          } else {
            this.options.splice(topicIndex, 1, data[0])
          }
        })
      },
      removeMedia() {
        this.linkPreview.video_url = ''
        this.linkPreview.image = ''
        this.linkPreview.image_url = ''
      },
      removePreview() {
        this.linkPreview = {}
      },
      removeTopics() {
        this.options = filter(this.options, o => o.identifier !== 'topics')
      },
      removeFile(id) {
        this.post.attachments = filter(this.post.attachments, a => a.id !== id)
      },
      async getPreview(link) {
        changeBool(this, 'isPendingPreview')
        await api.items.getLinkPreview(link).then(({ data }) => {
          changeBool(this, 'isPendingPreview')
          this.linkPreview = data.data
        }).catch(_ => {
          changeBool(this, 'isPendingPreview')
        })
      },
      updateLink(value) {
        this.link = value
      },
      async onSubmit() {
        changeBool(this, 'isPendingEdit')
        const postObj = {
          description: this.post.content,
          files: this.allFiles,
          options: this.postOptions,
          preview: this.linkPreview,
        }
        if (this.selectedAuthor) {
          postObj['author'] = this.selectedAuthor.id
        }
        await api.items.editItem(`posts.${this.post.id}`, postObj).then(_ => {
          changeBool(this, 'isPendingEdit')
          this.$emit('afterSubmit')
          this.$emit('onCloseOverlay')
        }).catch(_ => {
          changeBool(this, 'isPendingEdit')
        })
      },
      onResultUpdate(id, args) {
        this.$set(this.postOptions, id, args)
      },
      onUpdateAuthor(args) {
        this.selectedAuthor = args
      },
      changeFiles(files) {
        this.files = clone(files)
      },
      canPostAsAuthor(user) {
        return Boolean(
        user &&
        find(
          user.permissions,
          p => [
            'superadmin',
            'post_as_author',
          ].indexOf(p) !== -1,
        ),
      )
      },
      selectEmoji(emoji) {
        insertEmoji(emoji, this, '$refs.message.innerValue')
      },
    },
  }
</script>
